import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { WHATSAPP_URL } from "../lib/constants";
import { usePageMeta } from "../lib/usePageMeta";
import {
  PartyPopper, Music4, Umbrella, Palmtree, ShieldCheck, FileCheck2,
  ClipboardList, Award, Wallet, Users, Star, Camera, ArrowRight, Sparkles,
  Plane, Compass, Heart,
} from "lucide-react";
import SchoolQuoteModal from "../components/SchoolQuoteModal";

const HERO_IMG = "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=1600&q=70";
const BEACH_IMG = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=900&q=70";
const PARTY_IMG = "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=900&q=70";
const CTA_IMG = "https://images.unsplash.com/photo-1519074002996-a69e7ac46a42?auto=format&fit=crop&w=1600&q=70";

const PILLARS = [
  {
    icon: Palmtree,
    title: "Destinos com energia certa",
    body: "Camboriú, Porto Seguro, Salvador, Cancún, Punta Cana e Buenos Aires — os pontos que a turma pediu, com hospedagem selecionada e passeios sob medida.",
  },
  {
    icon: Music4,
    title: "Ativações fechadas pra vocês",
    body: "Open bar, DJ da turma, festa temática, boat party e day-use exclusivo. Tudo negociado direto com casas parceiras e liberado para menor de idade sob termo.",
  },
  {
    icon: ShieldCheck,
    title: "Segurança sem esquema",
    body: "Staff Orion 24h no destino, coordenadora de plantão, protocolo de emergência médica e psicológica, alcoolímetro do transporte e checklist antes de cada saída.",
  },
  {
    icon: Wallet,
    title: "Parcelamento em até 24×",
    body: "Financiamento próprio via parceiros Orion — a agência gerencia todo o fluxo financeiro para que a turma se preocupe só com o sonho, não com a planilha.",
  },
  {
    icon: Camera,
    title: "Cobertura da viagem",
    body: "Foto e vídeo profissional inclusos no pacote — o aftermovie fica pronto em 15 dias e o álbum digital chega no Google Drive da turma.",
  },
  {
    icon: Heart,
    title: "Sonho realizado, do começo ao fim",
    body: "Reuniões periódicas com os organizadores da turma alinhando cada detalhe — do primeiro sonho à ficha técnica de embarque. Zero surpresa faltando 30 dias.",
  },
];

const CASES = [
  { title: "Ensino Médio · 3º ano", body: "Camboriú 7 noites, all-inclusive + open bar e Beto Carrero — 120 formandos, parcelamento em 22× no boleto." },
  { title: "Graduação · Direito", body: "Buenos Aires 5 dias, roteiro cultural + jantar de gala no Recoleta, +2 dias em Punta del Este — 60 formandos." },
  { title: "Formatura Técnica", body: "Porto Seguro 5 noites, resort com day-use e balada Axé Moi — 85 formandos e 4 organizadores da turma." },
  { title: "Ensino Médio · 3º ano", body: "Cancún 7 noites all-inclusive premium, party boat exclusivo — 90 formandos, financiamento em 24×." },
];

const AGENDA = [
  "Reunião de kick-off com os organizadores para desenhar o sonho da turma",
  "Acompanhamento contínuo da agência com relatórios de andamento por formando",
  "Contrato individual formando ↔ Orion (CDC-compliant e reembolsável)",
  "Staff Orion embarcado no destino em regime 24h",
  "Cobertura médica, psicológica e brigadista incluída no pacote",
  "Aftermovie profissional em até 15 dias após a volta — a viagem eternizada",
];

const TRUST = [
  { icon: ShieldCheck, title: "Cadastur ativo", body: "Ministério do Turismo." },
  { icon: FileCheck2, title: "Contrato formal", body: "CDC + política clara." },
  { icon: ClipboardList, title: "Nota fiscal", body: "Emissão para responsável." },
  { icon: Award, title: "Empresa regularizada", body: "CNPJ 37.476.039/0001-90." },
];

export default function Formaturas() {
  const [quoteOpen, setQuoteOpen] = React.useState(false);
  usePageMeta(
    "Viagens de Formatura · Viagens Orion",
    "Viagens de formatura com curadoria de destinos, staff embarcado 24h, ativações fechadas para a turma e parcelamento em até 24×. Camboriú, Porto Seguro, Cancún, Punta Cana. Contrato blindado + Cadastur ativo."
  );

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1" data-testid="formaturas-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#4a1f3d] via-[#2C1139] to-[#0d0413] text-white">
          <span className="absolute inset-0 opacity-30" aria-hidden style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }} />
          <span className="absolute inset-0 bg-gradient-to-b from-[#2C1139]/95 via-[#2C1139]/75 to-[#0d0413]/85" aria-hidden />
          <span className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-orion-gold/25 blur-3xl" aria-hidden />

          <div className="relative container-orion py-16 md:py-28 max-w-4xl text-center mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-5">
              <PartyPopper size={13} /> Viagens Orion · Formaturas
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] mx-auto">
              A formatura vira <span className="text-orion-gold">memória para sempre</span> — tenha as melhores experiências registradas.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 mx-auto leading-relaxed">
              Destinos exclusivos, ativações fechadas para a turma, staff embarcado 24h
              e parcelamento em até 24×. Contrato blindado, Empresa Credenciada pelo Cadastur
              e videomaker acompanhando de perto do embarque ao aftermovie. Tudo isso com
              relatórios e reuniões com os organizadores selecionados.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-orion-gold text-orion-purple px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#c19a3f] transition-all shadow-[0_14px_38px_rgba(216,167,73,0.45)] hover:-translate-y-0.5"
                data-testid="form-cta-wa"
              >
                <ClipboardList size={14} /> Solicitar Cotação da Formatura
              </button>
              <a href="#experiencias" className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:border-orion-gold hover:text-orion-gold transition-colors">
                Ver Experiências <ArrowRight size={14} />
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-[10px] uppercase tracking-[0.3em] text-white/75">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={12} className="text-orion-gold" /> Cadastur ativo</span>
              <span className="inline-flex items-center gap-2"><FileCheck2 size={12} className="text-orion-gold" /> Contrato blindado</span>
              <span className="inline-flex items-center gap-2"><Wallet size={12} className="text-orion-gold" /> Parcela em 24×</span>
              <span className="inline-flex items-center gap-2"><Users size={12} className="text-orion-gold" /> Staff 24h no destino</span>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="border-y border-[#EFEAE0] bg-[#FAF7F0]">
          <div className="container-orion py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {TRUST.map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`form-trust-${i}`}>
                <t.icon size={22} className="text-orion-orange" />
                <div className="font-serif text-orion-purple text-sm md:text-base">{t.title}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#594A63] leading-snug">{t.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PILLARS */}
        <section id="experiencias" className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-14 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">O que faz a formatura Orion diferente</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Muito além de sair da cidade.
            </h2>
            <p className="mt-4 text-[#594A63] text-base md:text-lg leading-relaxed">
              A gente cuida da logística, do planejamento dos destinos, da negociação com casas
              noturnas, da segurança quando necessário, do contrato individual e dos recebimentos.
              A turma foca em curtir — o resto é com a gente.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="group border border-[#EFEAE0] p-7 hover:border-orion-orange hover:shadow-[0_18px_40px_rgba(44,17,57,0.12)] transition-all bg-white text-center"
                data-testid={`form-pillar-${i}`}
              >
                <p.icon size={30} className="text-orion-orange mb-4 mx-auto group-hover:scale-110 transition-transform" strokeWidth={1.6} />
                <h3 className="font-serif text-xl text-orion-purple mb-3 leading-snug">{p.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* HOW WE OPERATE */}
        <section className="bg-[#2C1139] text-white overflow-hidden">
          <div className="container-orion py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
            <div className="grid grid-cols-2 gap-4 order-2 lg:order-1">
              <img src={BEACH_IMG} alt="Praia da formatura" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)] mt-8" />
              <img src={PARTY_IMG} alt="Festa da turma de formatura" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)]" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Como operamos</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05]">
                Profissionais capacitados para realizar o sonho dos formandos e deixar pais satisfeitos.
              </h2>
              <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
                Cada formatura opera com a <strong>gestão da agência acompanhando de perto</strong> —
                garantindo que o sonho da turma vire realidade dentro do prazo, do padrão de
                qualidade e da segurança que os pais confiam. Cada formando assina seu próprio
                contrato — as regras ficam claras desde o primeiro dia.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/85">
                {AGENDA.map((li, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-orion-gold mt-1">›</span> {li}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* CASES / EXAMPLES */}
        <section className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-12 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Casos recentes</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Formaturas que a Orion assinou em 2025.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CASES.map((c, i) => (
              <div key={i} className="p-7 border-t-4 border-orion-orange bg-[#FAF7F0] text-center" data-testid={`form-case-${i}`}>
                <Star size={22} className="text-orion-orange mb-3 mx-auto" strokeWidth={1.6} />
                <h3 className="font-serif text-lg text-orion-purple mb-2 leading-snug">{c.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative bg-gradient-to-br from-orion-gold via-[#c19a3f] to-[#8a6d24] text-white overflow-hidden">
          <span className="absolute inset-0 opacity-25" aria-hidden style={{ backgroundImage: `url(${CTA_IMG})`, backgroundSize: "cover" }} />
          <span className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden />
          <div className="relative container-orion py-20 md:py-28 text-center max-w-3xl">
            <Sparkles size={30} className="mx-auto text-white mb-5" />
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">
              Bora fechar a formatura da sua turma?
            </h2>
            <p className="mt-5 text-white/95 text-base md:text-lg">
              Envie o curso, o número de formandos e o mês da viagem — a equipe da Orion
              volta em minutos com destino sugerido, valor por formando e opções de parcelamento.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-white text-orion-purple px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-orion-cream transition-all hover:-translate-y-0.5 shadow-[0_14px_38px_rgba(0,0,0,0.25)]"
                data-testid="form-cta-bottom"
              >
                <ClipboardList size={14} /> Solicitar Cotação da Formatura
              </button>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white/10 transition-colors"
              >
                Conhecer a Orion
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      {quoteOpen && <SchoolQuoteModal onClose={() => setQuoteOpen(false)} variant="formatura" />}
    </div>
  );
}
